import { useEffect, useMemo, useState } from 'react';
import { UserPlus, Copy, Trash2, Loader2, ShieldCheck, Camera, Clock, Ban, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { adminListTeam, adminListStaffInvites, adminCreateStaffInvite, adminRevokeStaffInvite } from '@/lib/api';
import { ADMIN_TEAM, ADMIN_TEAM_INVITE_KIND } from '@/constants/testIds';

const EXPIRY_OPTIONS = [
  { value: '24', label: '1 day' },
  { value: '72', label: '3 days' },
  { value: '168', label: '7 days' },
  { value: '720', label: '30 days' },
];

const isExpired = (iso) => new Date(iso) < new Date();

// "2h ago", "3 days ago", etc. — good enough for an activity glance; the exact
// timestamp is exposed via title on hover.
const relativeTime = (iso) => {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.round(diffMs / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
};

const isActiveRecently = (iso) => iso && Date.now() - new Date(iso).getTime() < 7 * 24 * 60 * 60 * 1000;

export const AdminTeamTab = () => {
  const [team, setTeam] = useState(null);
  const [invites, setInvites] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [expiresIn, setExpiresIn] = useState('168');
  const [inviteKind, setInviteKind] = useState('team');
  const [generating, setGenerating] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState('');
  const [query, setQuery] = useState('');

  const refresh = () => {
    adminListTeam().then(setTeam).catch(() => setTeam([]));
    adminListStaffInvites().then(setInvites).catch(() => setInvites([]));
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const invite = await adminCreateStaffInvite(Number(expiresIn), inviteEmail.trim() || undefined, inviteKind);
      setLastInviteUrl(invite.invite_url);
      toast.success('Invite link generated.');
      setInviteEmail('');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate invite.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Invite link copied.');
    } catch {
      toast.error('Could not copy link.');
    }
  };

  const handleRevoke = async (invite) => {
    if (!window.confirm('Revoke this invite link? It will no longer be usable.')) return;
    try {
      await adminRevokeStaffInvite(invite.id);
      toast.success('Invite revoked.');
      refresh();
    } catch {
      toast.error('Failed to revoke invite.');
    }
  };

  const pendingInvites = (invites || []).filter((i) => !i.used);

  const filteredTeam = useMemo(() => {
    if (!team) return team;
    const q = query.trim().toLowerCase();
    if (!q) return team;
    return team.filter((m) => (m.name || '').toLowerCase().includes(q) || m.email.toLowerCase().includes(q));
  }, [team, query]);

  return (
    <div data-testid={ADMIN_TEAM.section}>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[hsl(var(--lxra-muted))]">
          Studio access is invite-only. Generate a one-time link so a photographer can join the team dashboard.
        </p>
        <Button
          onClick={() => { setLastInviteUrl(''); setDialogOpen(true); }}
          data-testid={ADMIN_TEAM.inviteButton}
          className="h-10 px-4 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
        >
          <UserPlus className="h-4 w-4 mr-1.5" />
          Invite Photographer
        </Button>
      </div>

      {!team && <Skeleton className="h-40 rounded-2xl bg-[#141416] mb-6" />}

      {team && (
        <>
          <div className="relative mb-3 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--lxra-muted))]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search team by name or email…"
              data-testid={ADMIN_TEAM.searchInput}
              className="h-9 pl-9 bg-[#141416] border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))]"
            />
          </div>
          <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden mb-8">
            <Table>
              <TableHeader>
                <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                  <TableHead className="text-[hsl(var(--lxra-muted))]">Name</TableHead>
                  <TableHead className="text-[hsl(var(--lxra-muted))]">Email</TableHead>
                  <TableHead className="text-[hsl(var(--lxra-muted))]">Role</TableHead>
                  <TableHead className="text-[hsl(var(--lxra-muted))]">Last Login</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTeam.length === 0 && (
                  <TableRow className="border-[hsl(var(--lxra-border))]">
                    <TableCell colSpan={4} className="text-center text-[hsl(var(--lxra-muted))] py-8">
                      No team members match &quot;{query}&quot;.
                    </TableCell>
                  </TableRow>
                )}
                {filteredTeam.map((m) => (
                  <TableRow key={m.id} data-testid={ADMIN_TEAM.memberRow(m.id)} className="border-[hsl(var(--lxra-border))] hover:bg-[#141416]">
                    <TableCell className="text-[hsl(var(--lxra-fg))]">{m.name || '—'}</TableCell>
                    <TableCell className="text-[hsl(var(--lxra-fg))]">{m.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-[10px] gap-1 ${m.role === 'admin' ? 'border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))]' : 'border-sky-500/40 text-sky-400'}`}
                      >
                        {m.role === 'admin' ? <ShieldCheck className="h-3 w-3" /> : <Camera className="h-3 w-3" />}
                        {m.role === 'admin' ? 'Owner' : 'Photographer'}
                      </Badge>
                    </TableCell>
                    <TableCell
                      data-testid={ADMIN_TEAM.lastLogin(m.id)}
                      title={m.last_login_at ? new Date(m.last_login_at).toLocaleString() : 'Has not logged in yet'}
                      className="text-xs whitespace-nowrap"
                    >
                      {m.last_login_at ? (
                        <span className="inline-flex items-center gap-1.5 text-[hsl(var(--lxra-fg))]">
                          <span className={`h-1.5 w-1.5 rounded-full ${isActiveRecently(m.last_login_at) ? 'bg-emerald-400' : 'bg-[hsl(var(--lxra-muted))]'}`} />
                          {relativeTime(m.last_login_at)}
                        </span>
                      ) : (
                        <span className="text-[hsl(var(--lxra-muted))]">Never</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <h3 className="font-display text-lg tracking-[0.02em] text-[hsl(var(--lxra-fg))] mb-3">Pending Invites</h3>
      {!invites && <Skeleton className="h-32 rounded-2xl bg-[#141416]" />}
      {invites && pendingInvites.length === 0 && (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8 text-center text-sm text-[hsl(var(--lxra-muted))]">
          No pending invites. Generate one above to bring a photographer onto the team.
        </div>
      )}
      {invites && pendingInvites.length > 0 && (
        <div className="space-y-2">
          {pendingInvites.map((inv) => {
            const expired = isExpired(inv.expires_at);
            return (
              <div
                key={inv.id}
                data-testid={ADMIN_TEAM.pendingInviteRow(inv.id)}
                className="flex items-center justify-between gap-4 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm text-[hsl(var(--lxra-fg))] truncate">{inv.invite_url}</p>
                  <p className="text-xs text-[hsl(var(--lxra-muted))] mt-0.5 flex items-center gap-1.5">
                    {expired ? (
                      <span className="inline-flex items-center gap-1 text-red-400"><Ban className="h-3 w-3" /> Expired</span>
                    ) : (
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Expires {new Date(inv.expires_at).toLocaleString()}</span>
                    )}
                    {inv.email && <span>· Restricted to {inv.email}</span>}
                    <span data-testid={ADMIN_TEAM_INVITE_KIND.badge(inv.id)} className="rounded-full border border-[hsl(var(--lxra-border))] px-2 py-0.5 text-[10px] uppercase tracking-[0.08em]">{inv.kind === 'marketplace' ? 'Marketplace' : 'Team'}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleCopy(inv.invite_url)}
                    className="text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRevoke(inv)}
                    data-testid={ADMIN_TEAM.inviteRevokeButton(inv.id)}
                    className="text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]" data-testid={ADMIN_TEAM.inviteDialog}>
          <DialogHeader>
            <DialogTitle className="text-[hsl(var(--lxra-fg))]">Invite a Photographer</DialogTitle>
            <DialogDescription className="text-[hsl(var(--lxra-muted))]">
              Generate a one-time link so a photographer can join the studio dashboard.
            </DialogDescription>
          </DialogHeader>
          {!lastInviteUrl ? (
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Restrict to Email (optional)</Label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="photographer@example.com"
                  data-testid={ADMIN_TEAM.inviteEmailInput}
                  className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10"
                />
                <p className="text-[11px] text-[hsl(var(--lxra-muted))]">Leave blank to allow any email to redeem this link.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Invite Type</Label>
                <Select value={inviteKind} onValueChange={setInviteKind}>
                  <SelectTrigger data-testid={ADMIN_TEAM_INVITE_KIND.select} className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                    <SelectItem value="team">LXRA teammate — works inside the LXRA studio</SelectItem>
                    <SelectItem value="marketplace">Marketplace photographer — own studio, pre-approved</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[hsl(var(--lxra-muted))]">
                  {inviteKind === 'team' ? 'Teammates share LXRA galleries, bookings and clients.' : 'Marketplace photographers get their own private dashboard and a public /p/ profile, approved on signup.'}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Link Expires In</Label>
                <Select value={expiresIn} onValueChange={setExpiresIn}>
                  <SelectTrigger data-testid={ADMIN_TEAM.inviteExpirySelect} className="bg-[#141416] border-[hsl(var(--lxra-border))] h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                    {EXPIRY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={generating}
                  data-testid={ADMIN_TEAM.inviteGenerateButton}
                  className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Generate Invite Link'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-[hsl(var(--lxra-muted))]">Share this link with your photographer. It can only be used once.</p>
              <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--lxra-border))] bg-[#141416] px-3 py-2">
                <p data-testid={ADMIN_TEAM.inviteUrlText} className="text-xs text-[hsl(var(--lxra-fg))] truncate flex-1">{lastInviteUrl}</p>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleCopy(lastInviteUrl)}
                  data-testid={ADMIN_TEAM.inviteCopyButton}
                  className="text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] shrink-0"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => setDialogOpen(false)}
                  className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
                >
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
